import { parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Column } from '@tempmee/react';
import { FormActions, FormErrorBanner, useScrollToError } from '~/components/shared/forms';
import {
  SectionCard,
  ShiftRequestHeader,
  WizardNavigationGuard,
} from '~/components/ShiftRequestRedesign/common';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { useNavigationDetector } from '~/hooks/useNavigationDetector';
import { fireShiftDetailsPageViewed, fireShiftDetailsSubmitted } from '~/posthogs/events';
import { useIsFormValid } from '../hooks/useIsFormValid';
import { ShiftDetailsForm, useShiftDetailsForm } from './ShiftDetailsForm';
import { useSafeShiftDetailsLocationState } from './useSafeLocationState';

export const ShiftDetailsScreen = () => {
  const { control, watch, setValue, getValues } = useShiftDetailsForm();
  const isFormValid = useIsFormValid();
  const navigate = useNavigate();
  const { preselectedDate, professionType: navProfessionType } = useSafeShiftDetailsLocationState();

  const dates = watch('dates');

  const [bannerVisible, setBannerVisible] = useState(false);
  const bannerRef = useScrollToError(bannerVisible);
  const [showNavigationGuard, setShowNavigationGuard] = useState(false);

  const onNavigationAttempt = useCallback(() => {
    setShowNavigationGuard(true);
  }, []);

  const { allowNavigation, navigateToPending, clearPendingNavigation } = useNavigationDetector({
    when: true,
    onNavigationAttempt,
  });

  const handleBack = useCallback(() => {
    allowNavigation();
    navigate('/dashboard/shift-request/select-dates');
  }, [allowNavigation, navigate]);

  const handleContinue = useCallback(() => {
    if (!isFormValid) {
      setBannerVisible(true);
      return;
    }
    fireShiftDetailsSubmitted();
    allowNavigation();
    navigate('/dashboard/multi-day/add-invites');
  }, [isFormValid, allowNavigation, navigate]);

  useEffect(() => {
    if (preselectedDate) {
      const parsed = parseISO(preselectedDate);
      if (!isNaN(parsed.getTime())) {
        setValue('dates', [parsed]);
      }
    }
    if (navProfessionType) {
      const displayName =
        UserProfession.getName(navProfessionType) ||
        UserSubProfession.getName(navProfessionType) ||
        navProfessionType;
      setValue('professionType', displayName);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Run once on mount: seed form from navigation state

  useEffect(() => {
    fireShiftDetailsPageViewed();
  }, []);

  // Auto-clear the error banner once the form becomes valid
  useEffect(() => {
    if (isFormValid && bannerVisible) {
      setBannerVisible(false);
    }
  }, [isFormValid, bannerVisible]);

  useEffect(() => {
    if (dates.length === 0 && !preselectedDate) {
      allowNavigation();
      navigate('/dashboard/shift-request/select-dates', { replace: true });
    }
  }, [dates.length, allowNavigation, navigate, preselectedDate]);

  useEffect(() => {
    const overrideFields = [
      'preferredSpecialty',
      'isAssistedHygiene',
      'arrivalTime',
      'departureTime',
      'mealBreakDuration',
      'isMealBreakPaid',
      'rate',
      'procedures',
      'autoConfirm',
    ] as const;

    const subscription = watch((_, { name }) => {
      if (!name) return;

      const baseField = overrideFields.find(
        (field) => name === field || name.startsWith(`${field}.`),
      );
      if (!baseField) return;

      const overrides = getValues('shiftOverrides');
      if (!overrides || Object.keys(overrides).length === 0) return;

      const newValue = getValues(baseField);
      const updatedOverrides = Object.fromEntries(
        Object.entries(overrides).map(([dateKey, override]) => [
          dateKey,
          { ...override, [baseField]: newValue },
        ]),
      );

      setValue('shiftOverrides', updatedOverrides);
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, getValues]);

  return (
    <ShiftRequestHeader currentStep={1} onBack={handleBack} maxWidth={1120}>
      <Column style={{ maxWidth: 730, alignSelf: 'center' }} width="100%">
        <FormErrorBanner ref={bannerRef} visible={bannerVisible} />

        <Column gap={6}>
          <SectionCard title="Shift Details" testId="shift-details-card">
            <ShiftDetailsForm
              control={control}
              watch={watch}
              setValue={setValue}
              showFieldErrors={bannerVisible}
            />
          </SectionCard>
        </Column>
      </Column>

      <FormActions>
        <Button
          data-testid="continue-button"
          data-cy="continue-button"
          onClick={handleContinue}
          size="lg"
        >
          Continue
        </Button>
      </FormActions>

      <WizardNavigationGuard
        isOpen={showNavigationGuard}
        onStay={() => {
          clearPendingNavigation();
          setShowNavigationGuard(false);
        }}
        onLeave={navigateToPending}
      />
    </ShiftRequestHeader>
  );
};
